import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { Spinner } from "@/components/kit";
import { Textarea } from "@/components/ui/textarea";

export default function WatchVideo() {
  const [params] = useSearchParams();
  const token = params.get("t") || "";
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");
  const [sent, setSent] = useState(false);
  const watched = useRef(false);
  const base = `/year-video/${encodeURIComponent(token)}`;
  const onPlay = () => { if (watched.current) return; watched.current = true; api.post(`${base}/watched`).catch(() => {}); };
  const heart = async () => {
    setBusy("heart");
    try { await api.post(`${base}/heart`); setInfo((i) => ({ ...i, hearted: true })); toast.success(`Heart sent to ${info.member} ♥`); }
    catch (e) { toast.error(errMsg(e, "Could not send heart")); } finally { setBusy(""); }
  };
  const send = async () => {
    setBusy("note");
    try { await api.post(`${base}/note`, { message: note }); setSent(true); setNote(""); toast.success(`Note sent to ${info.member} 💌`); }
    catch (e) { toast.error(errMsg(e, "Could not send note")); } finally { setBusy(""); }
  };

  useEffect(() => {
    api.get(`/year-video/${encodeURIComponent(token)}/info`).then((r) => setInfo(r.data)).catch((e) => setError(errMsg(e, "This video is no longer available")));
  }, [token]);

  if (error) return <div className="min-h-screen flex items-center justify-center p-5"><div className="mm-card p-10 text-center" data-testid="watch-error"><div className="text-4xl">🎬</div><p className="mt-3">{error}</p></div></div>;
  if (!info) return <Spinner className="min-h-screen items-center" />;
  return (
    <div className="min-h-screen mm-gradient-soft px-5 py-12">
      <div className="max-w-lg mx-auto mm-fade-up" data-testid="watch-page">
        <span className="mm-chip !cursor-default !text-xs">🔒 Private link</span>
        <h1 className="text-4xl font-bold mt-4" data-testid="watch-title">{info.member}'s year</h1>
        <p className="text-mm-muted mt-1">{info.label}</p>
        {info.message && <p className="mm-soft p-4 mt-4 italic" data-testid="watch-message">“{info.message}” — {info.member}</p>}
        <video className="w-full rounded-2xl mt-5 bg-black" controls playsInline onPlay={onPlay} src={`${process.env.REACT_APP_BACKEND_URL}/api/year-video/${encodeURIComponent(token)}`} data-testid="watch-video" />
        <div className="mm-card p-5 mt-5" data-testid="watch-reactions">
          <div className="font-semibold">Let {info.member} know you watched</div>
          {info.hearted ? <p className="text-sm text-mm-primary font-semibold mt-3" data-testid="watch-hearted">♥ Heart sent</p> : (
            <button className="mm-btn mt-3 inline-flex items-center gap-2" disabled={busy === "heart"} onClick={heart} data-testid="watch-heart">
              {busy === "heart" && <Loader2 size={14} className="animate-spin" />} ♥ Heart
            </button>
          )}
          {sent ? <p className="text-sm mt-4" data-testid="watch-note-sent">Your note is on its way 💌</p> : (
            <>
              <Textarea className="mt-4" rows={3} placeholder="Write a short note (optional)" value={note} onChange={(e) => setNote(e.target.value.slice(0, 300))} data-testid="watch-note-input" />
              <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-mm-muted">{note.length}/300</span>
                <button className="mm-btn-ghost !py-2 !px-4 text-sm inline-flex items-center gap-2" disabled={!note.trim() || busy === "note"} onClick={send} data-testid="watch-note-send">
                  {busy === "note" && <Loader2 size={14} className="animate-spin" />} Send note
                </button>
              </div>
            </>
          )}
        </div>
        <p className="text-xs text-mm-muted mt-3">Shared with you through Mindful Me. Journal entries are never shared.</p>
      </div>
    </div>
  );
}
